export const BULLET_SIZE = 8

export const COLORS = [
    '#0074D9',
    '#FF4136',
    '#2ECC40',
    '#B10DC9',
    '#7FDBFF',
    '#FFDC00',
    '#FF851B',
    '#AAAAAA',
    '#39CCCC',
    '#001F3F',
    '#006400',
    '#8B0000',
    '#4B0082',
    '#008080',
    '#DAA520',
    '#800080',
    '#808080',
    '#ADD8E6',
    '#90EE90',
    '#FF6347',
    '#E6E6FA',
    '#20B2AA',
    '#FFFFE0',
    '#FFB6C1',
    '#D3D3D3',
    '#B0E0E6',
    '#E0FFFF',
    '#98FB98',
    '#FFA07A',
]

export const hexToDecimal = hex => {
    const matched = /#(\w{2})(\w{2})(\w{2})/g.exec(hex)
    
    return matched.splice(1, 3).map(g => parseInt(g, 16))
}

export const invertHex = hex => {
    const matched = /#(\w{2})(\w{2})(\w{2})/g.exec(hex)
    const groups = matched.splice(1, 3)
    
    return '#' + groups.map(g => (255 - parseInt(g, 16)).toString(16).padStart(2, '0')).join('')
}

export const toFixed = (value, decimals) => parseFloat(parseFloat(value).toFixed(decimals))

export const getCoords = plot => plot.geoJSON.metes_and_bounds.features
    .geometry.coordinates.map(coord => ([parseFloat(coord[0]), parseFloat(coord[1])]))

export const getMetesAndBounds = plot => {
    const coordinates = getCoords(plot)
    const properties = plot.geoJSON.metes_and_bounds.features.properties
    const metesAndBounds = []
    
    for (let idx = 0; idx < coordinates.length - 1; idx++) {
        const startingPoint = coordinates[idx]
        const endingPoint = coordinates[idx + 1]
        const mainDir = properties.main_directions[idx]
        const augDir = properties.augment_directions[idx]
        const augDeg = toFixed(properties.augment_degrees[idx], 2)
        const distance = toFixed(properties.distances[idx], 2)
        const unit = properties.unit
        const text = `${mainDir} ${augDeg} ${augDir}, ${distance} ${unit}`
        
        metesAndBounds.push({ startingPoint, endingPoint, text })
    }
    
    return metesAndBounds
}

export const setShadow = (ctx, { shadowColor, shadowBlur } = { shadowColor: '', shadowBlur: 0 }) => {
    ctx.shadowColor = shadowColor
    ctx.shadowBlur = shadowBlur
}

export const getEdges = (coords, threshold = 2) => {
    const minX = Math.min(...coords.map(v => v[0])) - threshold
    const minY = Math.min(...coords.map(v => v[1])) - threshold
    const maxX = Math.max(...coords.map(v => v[0])) + threshold
    const maxY = Math.max(...coords.map(v => v[1])) + threshold
    
    return { minX, minY, maxX, maxY }
}

export const drawEdges = (ctx, { minX, minY, maxX, maxY }, bulletSize = BULLET_SIZE) => {
    ctx.strokeRect(minX, minY, maxX - minX, maxY - minY)
    ctx.roundRect(minX - bulletSize / 2, minY - bulletSize / 2, bulletSize, bulletSize, bulletSize)
    ctx.roundRect(maxX - bulletSize / 2, minY - bulletSize / 2, bulletSize, bulletSize, bulletSize)
    ctx.roundRect(minX - bulletSize / 2, maxY - bulletSize / 2, bulletSize, bulletSize, bulletSize)
    ctx.roundRect(maxX - bulletSize / 2, maxY - bulletSize / 2, bulletSize, bulletSize, bulletSize)
    ctx.stroke()
}

export const withinBounds = ({ offsetX, offsetY }, { minX, minY, maxX, maxY }, threshold = 0) =>
    offsetX >= minX - threshold
    && offsetX <= maxX + threshold
    && offsetY >= minY - threshold
    && offsetY <= maxY + threshold

export const plotAndDownload = (plot, plotColor) => {
    const offset = 10
    // eslint-disable-next-line no-restricted-globals
    const canvas = document.createElement('canvas')
    
    const coords = getCoords(plot)
    const { minX, maxX, minY, maxY } = getEdges(coords, 0)
    
    canvas.width = 700
    canvas.height = 700 * (maxY - minY) / (maxX - minX)
    
    const ctx = canvas.getContext('2d')
    const decimalColors = hexToDecimal(plotColor)
    
    ctx.fillStyle = decimalColors.every(dc => dc > 255 / 2) ? 'black' : 'white'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    
    ctx.beginPath()
    ctx.lineWidth = 3
    ctx.lineJoin = 'round'
    ctx.strokeStyle = plotColor
    
    const cds = coords.map(cd => {
        return [
            (cd[0] - minX) / (maxX - minX) * (canvas.width - offset),
            (cd[1] - minY) / (maxY - minY) * (canvas.height - offset),
        ]
    })
    
    cds.forEach(point => ctx.lineTo(point[0] + offset / 2, point[1] + offset / 2))
    ctx.stroke()
    
    // eslint-disable-next-line no-restricted-globals
    const link = document.createElement('a')
    
    link.download = `${plot.documentName}.png`
    link.href = canvas.toDataURL()
    link.click()
}

export const closestNumbers = (value, list) => {
    let lower = value
    let upper = value
    
    list.forEach(v => {
        if (v < value)
            lower = v
        
        if (v > value && upper === value)
            upper = v
    })
    
    return [lower, upper]
}
